import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  Badge,
  Button,
  EmptyState,
  Spinner,
  Banner,
  Modal,
  FormLayout,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Divider,
} from "@shopify/polaris";
import {
  getRewardPolicies,
  createRewardPolicy,
  type RewardPolicy,
} from "../api/campaigns";
import { isShopInstallRedirectError } from "../api/admin-api-client";
import { RewardPolicyBuilder } from "../components/RewardPolicyBuilder/RewardPolicyBuilder";

function RewardsPage() {
  const [policies, setPolicies] = useState<RewardPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create policy modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState("");
  const [newPolicyConfig, setNewPolicyConfig] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRewardPolicies();
      setPolicies(data);
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load reward policies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const handleOpenCreateModal = () => {
    setNewPolicyName("");
    setNewPolicyConfig(null);
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewPolicyName("");
    setNewPolicyConfig(null);
  };

  const handleCreatePolicy = async () => {
    if (!newPolicyName.trim()) {
      setError("Please provide a name for the policy");
      return;
    }

    if (!newPolicyConfig || !newPolicyConfig.outcomes || newPolicyConfig.outcomes.length === 0) {
      setError("Please configure at least one outcome");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const policy = await createRewardPolicy({
        name: newPolicyName,
        policy: newPolicyConfig,
        expectedValue: 0,
      });
      setPolicies([policy, ...policies]);
      setSuccessMessage(`Reward policy "${newPolicyName}" created successfully`);
      handleCloseCreateModal();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to create reward policy");
    } finally {
      setCreating(false);
    }
  };

  const getOutcomeTypeBadge = (type: string) => {
    switch (type) {
      case "discount_code":
        return <Badge tone="success">Discount</Badge>;
      case "free_shipping":
        return <Badge tone="info">Free Shipping</Badge>;
      case "loyalty_points":
        return <Badge tone="warning">Points</Badge>;
      case "message":
        return <Badge>Message</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getOutcomeSummary = (policy: RewardPolicy) => {
    try {
      const config = typeof policy.policy === "string" 
        ? JSON.parse(policy.policy) 
        : policy.policy;
      const outcomes = config?.outcomes || [];
      const totalWeight = outcomes.reduce((sum: number, o: any) => sum + (o.weight || 0), 0);
      return { outcomes, totalWeight };
    } catch {
      return { outcomes: [], totalWeight: 0 };
    }
  };

  if (loading) {
    return (
      <Page
        title="Reward Policies"
      >
        <Card>
          <Box padding="800">
            <BlockStack align="center">
              <Spinner size="large" />
              <Text as="p" variant="bodyMd" tone="subdued">Loading policies...</Text>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (error && !isCreateModalOpen) {
    return (
      <Page
        title="Reward Policies"
        primaryAction={{ content: "Create policy", onAction: handleOpenCreateModal }}
      >
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
        <Card>
          <Box padding="800">
            <BlockStack align="center" gap="400">
              <Text as="p">Unable to load reward policies. Please try again.</Text>
              <Button onClick={loadPolicies}>Retry</Button>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (policies.length === 0 && !isCreateModalOpen) {
    return (
      <Page
        title="Reward Policies"
        primaryAction={{ content: "Create policy", onAction: handleOpenCreateModal }}
      >
        {successMessage && (
          <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
            {successMessage}
          </Banner>
        )}
        <Card>
          <EmptyState
            heading="No reward policies defined yet"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            action={{ content: "Create your first policy", onAction: handleOpenCreateModal }}
          >
            <p>
              Reward policies define what customers can win in your games.
              Create policies with different outcomes like discounts, free shipping, or loyalty points.
            </p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Reward Policies"
      subtitle={`${policies.length} polic${policies.length !== 1 ? "ies" : "y"} configured`}
      primaryAction={{ content: "Create policy", onAction: handleOpenCreateModal }}
    >
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {successMessage && (
        <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Banner>
      )}

      <BlockStack gap="400">
        {policies.map((policy) => {
          const { outcomes, totalWeight } = getOutcomeSummary(policy);
          const policyConfig = typeof policy.policy === "string" 
            ? JSON.parse(policy.policy) 
            : policy.policy;

          return (
            <Card key={policy.id}>
              <Box padding="400">
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack gap="200">
                      <Text as="h2" variant="headingMd" fontWeight="semibold">
                        {policy.name}
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Created: {new Date(policy.createdAt).toLocaleDateString()}
                      </Text>
                    </BlockStack>
                    <Badge tone="info">{`${outcomes.length} outcome${outcomes.length !== 1 ? "s" : ""}`}</Badge>
                  </InlineStack>

                  <Divider />

                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">Outcomes:</Text>
                    <InlineStack gap="200" wrap>
                      {outcomes.slice(0, 5).map((outcome: any, idx: number) => {
                        const probability = totalWeight > 0 
                          ? ((outcome.weight / totalWeight) * 100).toFixed(0) 
                          : "0";
                        return (
                          <InlineStack key={idx} gap="100" blockAlign="center">
                            {getOutcomeTypeBadge(outcome.type)}
                            <Text as="span" variant="bodySm" tone="subdued">
                              {outcome.key} ({probability}%)
                            </Text>
                          </InlineStack>
                        );
                      })}
                      {outcomes.length > 5 && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          +{outcomes.length - 5} more
                        </Text>
                      )}
                    </InlineStack>
                  </BlockStack>

                  {(policyConfig?.caps?.perCustomerPerDay || policyConfig?.caps?.perDevicePerDay) && (
                    <>
                      <Divider />
                      <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">Limits:</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {policyConfig.caps.perCustomerPerDay && (
                            <>Max {policyConfig.caps.perCustomerPerDay}/customer/day</>
                          )}
                          {policyConfig.caps.perCustomerPerDay && policyConfig.caps.perDevicePerDay && " • "}
                          {policyConfig.caps.perDevicePerDay && (
                            <>Max {policyConfig.caps.perDevicePerDay}/device/day</>
                          )}
                        </Text>
                      </BlockStack>
                    </>
                  )}

                  {policyConfig?.budget?.maxGrants && (
                    <>
                      <Divider />
                      <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">Budget:</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Max {policyConfig.budget.maxGrants} grants {policyConfig.budget.scope?.toLowerCase()}
                        </Text>
                      </BlockStack>
                    </>
                  )}
                </BlockStack>
              </Box>
            </Card>
          );
        })}

        <Card>
          <Box padding="400">
            <InlineStack align="center">
              <Text as="p" variant="bodySm" tone="subdued">
                Create multiple policies with different odds to A/B test reward strategies.
              </Text>
            </InlineStack>
          </Box>
        </Card>
      </BlockStack>

      {/* Create Policy Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title="Create Reward Policy"
        primaryAction={{
          content: creating ? "Creating..." : "Create Policy",
          onAction: handleCreatePolicy,
          loading: creating,
          disabled: !newPolicyName.trim(),
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: handleCloseCreateModal,
          },
        ]}
        size="large"
      >
        <Modal.Section>
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          <FormLayout>
            <TextField
              label="Policy Name"
              value={newPolicyName}
              onChange={setNewPolicyName}
              placeholder="e.g., Standard Rewards, Holiday Promo, VIP Rewards"
              autoComplete="off"
              helpText="Give your policy a descriptive name so you can easily identify it later"
            />
          </FormLayout>
        </Modal.Section>

        <Modal.Section>
          <RewardPolicyBuilder onChange={setNewPolicyConfig} />
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default RewardsPage;
